import React, { useContext } from 'react';
import { omit } from 'lodash';
import { styled } from '@mui/material';
import uuid from 'uuid';

import { useViewport } from 'contexts/viewport';
import { Button, Icon, IconButton, ResponsiveDialog, Stack, Text, TextInput } from 'shell/components';
import { PageContext } from 'pages/TaskManager/PageProvider';

const AddEditCriterionDialog = () => {
    const { device } = useViewport();
    const { setFormativeForm, addEditCriteriaDialog, setAddEditCriteriaDialog } = useContext(PageContext);
    const { open, criterionId, title, description } = addEditCriteriaDialog;

    const handleClose = () => {
        setAddEditCriteriaDialog((current) => ({
            ...current,
            open: false,
        }));
    };

    const handleChange = (e) => {
        setAddEditCriteriaDialog((current) => ({ ...current, [e.target.name]: e.target.value }));
    };

    const handleClickSubmit = () => {
        if (!criterionId) {
            const newCriterion = {
                id: uuid.v4(),
                title,
                description,
                options: [
                    {
                        id: uuid.v4(),
                        title: '',
                        description: '',
                        rate: '',
                    },
                    {
                        id: uuid.v4(),
                        title: '',
                        description: '',
                        rate: '',
                    },
                ],
            };
            setFormativeForm((current) => ({ ...current, rubric: [...current.rubric, newCriterion] }));
        } else {
            setFormativeForm((current) => {
                return {
                    ...current,
                    rubric: current.rubric.map((criterion) => {
                        if (criterion.id !== criterionId) return criterion;
                        return {
                            ...criterion,
                            title,
                            description,
                        };
                    }),
                };
            });
        }
        handleClose();
    };

    const handleClickDelete = () => {
        setFormativeForm((current) => {
            return {
                ...current,
                rubric: current.rubric.filter(({ id }) => id !== criterionId),
                grading: omit(current.grading, [criterionId]),
            };
        });
        handleClose();
    };

    return (
        <CustomResponsiveDialog device={device} open={open} onClose={handleClose}>
            <Stack spacing={6}>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text variant={'h6'} weight={'semibold'}>
                        {!Boolean(criterionId) ? 'Add' : 'Edit'} Criterion
                    </Text>
                    <IconButton kind={'exit'} size={'medium'} variant={'square'} onClick={handleClose} />
                </Stack>
                <TextInput
                    label={'Criterion Name'}
                    name={'title'}
                    placeholder={'ex. Communication'}
                    value={title}
                    onChange={handleChange}
                />
                <CustomTextInput
                    label={'Description'}
                    helperText={`${description.length}/200 characters`}
                    name={'description'}
                    placeholder={'Give a brief description of what this criterion means.'}
                    value={description}
                    onChange={handleChange}
                />
                <Stack direction={'row-reverse'} justifyContent={'space-between'}>
                    <Stack direction={'row'} spacing={4}>
                        <Button color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleClickSubmit}>
                            {!Boolean(criterionId) ? 'Create' : 'Update'} Criterion
                        </Button>
                    </Stack>
                    {Boolean(criterionId) && (
                        <Button
                            color={'neutral'}
                            startIcon={<Icon kind={'delete'} />}
                            variant={'tertiary'}
                            onClick={handleClickDelete}
                        >
                            Delete
                        </Button>
                    )}
                </Stack>
            </Stack>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiDialog-paper': {
            width: '570px',
        },
    };
});

const CustomTextInput = styled(TextInput)(() => {
    return {
        '& .MuiFormHelperText-root': {
            textAlign: 'right',
        },
    };
});

export default AddEditCriterionDialog;
